import React, { useState, useEffect, useContext } from 'react';
import './DailyPanchang.css';
import { LanguageContext } from "../context/LanguageContext";

export default function DailyPanchang() {
  const [isOpen, setIsOpen] = useState(false);
  const [panchang, setPanchang] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { lang } = useContext(LanguageContext);

  const toDevanagariNumber = (num) => {
    if (typeof num !== 'string' && typeof num !== 'number') return '';
    const d = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
    return num.toString().split('').map(x => d[x] || x).join('');
  };

  const fromDevanagariNumber = (str) => {
    if (typeof str !== 'string') return str;
    const d = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
    let s = str;
    for (let i = 0; i < 10; i++) s = s.replace(new RegExp(d[i], 'g'), String(i));
    return s;
  };

  const getDisplayDate = () => {
    const date = new Date();
    const day = date.getDate();
    const monthIndex = date.getMonth();
    const monthNames = {
      en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      mr: ['जाने', 'फेब्रु', 'मार्च', 'एप्रिल', 'मे', 'जून', 'जुलै', 'ऑग', 'सप्टें', 'ऑक्टो', 'नोव्हें', 'डिसें']
    };
    return { day, month: { en: monthNames.en[monthIndex], mr: monthNames.mr[monthIndex] } };
  };

  const dateInfo = getDisplayDate();

  useEffect(() => {
    const fetchPanchang = async () => {
      setLoading(true);
      try {
        const res = await fetch('https://itv.dindoripranit.org/api/v1/panchang/today', { headers: { 'accept': '*/*' } });
        const json = await res.json();
        if (json.isSuccess && json.value) {
          setPanchang(json.value);
        }
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchPanchang();
  }, []);

  const labels = {
    date: { en: 'Date', mr: 'दिनांक' },
    savastar: { en: 'Samvatsar', mr: 'संवत्सर' },
    shake: { en: 'Shak', mr: 'शक' },
    vaar: { en: 'Day', mr: 'वार' },
    ayan: { en: 'Ayan', mr: 'आयन' },
    ritu: { en: 'Season', mr: 'ऋतू' },
    maas: { en: 'Month', mr: 'मास' },
    paksha: { en: 'Paksha', mr: 'पक्ष' },
    tithi: { en: 'Tithi', mr: 'तिथी' },
    nakshatra: { en: 'Nakshatra', mr: 'नक्षत्र' },
    yog: { en: 'Yoga', mr: 'योग' },
    karan: { en: 'Karan', mr: 'करण' },
    chandra_rashi: { en: 'Moon Sign', mr: 'चंद्र राशी' },
    surya_rashi: { en: 'Sun Sign', mr: 'सूर्य राशी' },
    sunrise: { en: 'Sunrise', mr: 'सूर्योदय' },
    sunset: { en: 'Sunset', mr: 'सूर्यास्त' },
    moonrise: { en: 'Moonrise', mr: 'चंद्रोदय' },
    moonset: { en: 'Moonset', mr: 'चंद्रास्त' },
    rahukaal: { en: 'Rahukaal', mr: 'राहुकाल' },
    guruwani: { en: "Guru's Message", mr: 'गुरुवाणी' },
    noData: { en: 'Data not available for today', mr: 'आजसाठी डेटा उपलब्ध नाही' }
  };

  const formatValue = (val) => {
    if (!val) return '';
    return val;
  };

  const formatTime12Hr = (time24) => {
    if (!time24) return '';
    const parts = time24.split(':');
    let hours = parseInt(parts[0], 10);
    const minutes = parts[1];
    const seconds = parts[2];
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${hours}:${minutes}:${seconds} ${ampm}`;
  };

  const formatWithEndTime = (value, endTime) => {
    if (!value) return '';
    return `${value} ${endTime} पर्यंत`;
  };

  const formatRahukaal = (marathiText) => {
    if (!marathiText) return '';
    let englishText = marathiText;
    const timeOfDay = { 'दुपारी': 'Afternoon', 'सकाळी': 'Morning', 'सायं.': 'Evening' };
    for (const [mr, en] of Object.entries(timeOfDay)) {
      englishText = englishText.replace(mr, en);
    }
    englishText = englishText.replace('ते', 'to');
    englishText = fromDevanagariNumber(englishText);
    return `${englishText} (${marathiText})`;
  };

  const formatDualLanguageDate = (dateString) => {
    if (!dateString) return '';
    const parts = dateString.split('-');
    const [year, month, day] = parts;
    const enDate = `${day}-${month}-${year}`;
    const mrDate = `${toDevanagariNumber(day)}-${toDevanagariNumber(month)}-${toDevanagariNumber(year)}`;
    return `${enDate} (${mrDate})`;
  };

  return (
    <>
      <div className="panchang-card" onClick={() => setIsOpen(true)}>
        <div className="panchang-icon">
          <span className="calendar-month">{dateInfo.month[lang]}</span>
          <span className="calendar-day">{lang === 'mr' ? toDevanagariNumber(dateInfo.day) : dateInfo.day}</span>
        </div>
        <div>
          <strong>{lang === 'en' ? 'Daily Panchang' : 'दैनिक पंचांग'}</strong>
          <p>{lang === 'en' ? 'Click to view details' : 'तपशील पाहण्यासाठी क्लिक करा'}</p>
        </div>
      </div>

      {isOpen && (
        <div className="panchang-modal-backdrop" onClick={() => setIsOpen(false)}>
          <div className="panchang-modal" onClick={(e) => e.stopPropagation()}>
            <header>
              <h2>Daily Panchang (दैनिक पंचांग)</h2>
              <button className="close-btn" onClick={() => setIsOpen(false)}>{"\u00D7"}</button>
            </header>
            <div className="panchang-content">
              {loading && <p>Loading...</p>}
              {error && <p>Error loading panchang data.</p>}
              {!loading && !error && !panchang && (
                <p>{labels.noData.en} / {labels.noData.mr}</p>
              )}
              {!loading && !error && panchang && (
                <table>
                  <tbody>
                    <tr>
                      <td>{labels.date.en} ({labels.date.mr})</td>
                      <td>{formatDualLanguageDate(panchang.date)}</td>
                    </tr>
                    <tr>
                      <td>{labels.savastar.en} ({labels.savastar.mr})</td>
                      <td>{formatValue(panchang.savastar)}</td>
                    </tr>
                    <tr>
                      <td>{labels.shake.en} ({labels.shake.mr})</td>
                      <td>{formatValue(panchang.shake)}</td>
                    </tr>
                    <tr>
                      <td>{labels.vaar.en} ({labels.vaar.mr})</td>
                      <td>{formatValue(panchang.vaar)}</td>
                    </tr>
                    <tr>
                      <td>{labels.ayan.en} ({labels.ayan.mr})</td>
                      <td>{formatValue(panchang.ayan)}</td>
                    </tr>
                    <tr>
                      <td>{labels.ritu.en} ({labels.ritu.mr})</td>
                      <td>{formatValue(panchang.ritu)}</td>
                    </tr>
                    <tr>
                      <td>{labels.maas.en} ({labels.maas.mr})</td>
                      <td>{formatValue(panchang.maas)}</td>
                    </tr>
                    <tr>
                      <td>{labels.paksha.en} ({labels.paksha.mr})</td>
                      <td>{formatValue(panchang.paksha)}</td>
                    </tr>
                    <tr>
                      <td>{labels.tithi.en} ({labels.tithi.mr})</td>
                      <td>{formatWithEndTime(panchang.tithi, panchang.tithiEndTime)}</td>
                    </tr>
                    <tr>
                      <td>{labels.nakshatra.en} ({labels.nakshatra.mr})</td>
                      <td>{formatWithEndTime(panchang.nakshatra, panchang.nakshatraEndTime)}</td>
                    </tr>
                    <tr>
                      <td>{labels.yog.en} ({labels.yog.mr})</td>
                      <td>{formatWithEndTime(panchang.yog, panchang.yogEndTime)}</td>
                    </tr>
                    <tr>
                      <td>{labels.karan.en} ({labels.karan.mr})</td>
                      <td>{formatWithEndTime(panchang.karan, panchang.karanEndTime)}</td>
                    </tr>
                    <tr>
                      <td>{labels.chandra_rashi.en} ({labels.chandra_rashi.mr})</td>
                      <td>{formatValue(panchang.chandra_Rashi)}</td>
                    </tr>
                    <tr>
                      <td>{labels.surya_rashi.en} ({labels.surya_rashi.mr})</td>
                      <td>{formatValue(panchang.surya_Rashi)}</td>
                    </tr>
                    <tr>
                      <td>{labels.sunrise.en} ({labels.sunrise.mr})</td>
                      <td>{formatTime12Hr(panchang.sunrise)}</td>
                    </tr>
                    <tr>
                      <td>{labels.sunset.en} ({labels.sunset.mr})</td>
                      <td>{formatTime12Hr(panchang.sunset)}</td>
                    </tr>
                    <tr>
                      <td>{labels.moonrise.en} ({labels.moonrise.mr})</td>
                      <td>{formatTime12Hr(panchang.moonrise)}</td>
                    </tr>
                    <tr>
                      <td>{labels.moonset.en} ({labels.moonset.mr})</td>
                      <td>{formatTime12Hr(panchang.moonset)}</td>
                    </tr>
                    <tr>
                      <td>{labels.rahukaal.en} ({labels.rahukaal.mr})</td>
                      <td>{formatRahukaal(panchang.rahukaal)}</td>
                    </tr>
                    {panchang.guruwani && (
                      <tr>
                        <td colSpan="2" style={{ fontStyle: 'italic', paddingTop: '12px' }}>
                          <strong>{labels.guruwani.en} ({labels.guruwani.mr}):</strong><br />
                          {formatValue(panchang.guruwani)}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}